// src/pages/About/index.jsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Space, 
  Avatar, 
  Row, 
  Col,
  Form,
  Input,
  Button,
  Upload,
  message,
  Tabs
} from 'antd';
import { 
  UserOutlined,
  SaveOutlined,
  CameraOutlined,
  BookOutlined,
  CalendarOutlined,
  LockOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import './index.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const About = () => {
  const [form] = Form.useForm();
  const [avatar, setAvatar] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [postsCount, setPostsCount] = useState(0);
  
  useEffect(() => {
    // 从localStorage加载用户信息
    const savedUsername = localStorage.getItem('username') || '用户';
    const savedAvatar = localStorage.getItem('avatar') || '';
    
    setUsername(savedUsername);
    setAvatar(savedAvatar);
    
    // 设置表单初始值
    form.setFieldsValue({
      username: savedUsername
    });
    
    // 获取文章数量
    const posts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
    setPostsCount(posts.length);
  }, [form]);

  // 处理头像上传
  const handleAvatarUpload = (info) => {
    if (info.file.status === 'done') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Image = e.target.result;
        setAvatar(base64Image);
        localStorage.setItem('avatar', base64Image);
        message.success('头像更新成功！');
      };
      reader.readAsDataURL(info.file.originFileObj);
    }
  };

  // 保存个人信息
  const handleSaveProfile = (values) => {
    setLoading(true);
    
    setTimeout(() => {
      localStorage.setItem('username', values.username);
      if (avatar) {
        localStorage.setItem('avatar', avatar);
      }
      message.success('个人信息已保存！');
      setUsername(values.username);
      setLoading(false);
    }, 800);
  };

  // 修改密码
  const handleChangePassword = (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的密码不一致！');
      return;
    }
    
    // 这里应该验证旧密码，简化处理直接修改
    message.success('密码修改成功！');
    form.resetFields(['oldPassword', 'newPassword', 'confirmPassword']);
  };

  // 模拟注册时间
  const registrationDate = '2024-01-15';

  return (
    <div 
      className="profile-page"
      style={{
        position: 'relative',
        minHeight: '100vh',
        zIndex: 1,
      }}
    >
      {/* 视频背景 */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -1,
        }}
      >
        <source src={`${process.env.PUBLIC_URL}/WeChat_20241014205858.mp4`} type="video/mp4" />
        您的浏览器不支持视频播放。
      </video>
      
      {/* 主要内容 - 半透明效果 */}
      <div 
        className="page-content"
        style={{
          width: '100%',
          maxWidth: '95%',
          margin: '0 auto',
          padding: '20px 0 60px',
          minHeight: '100vh',
          backgroundColor: 'rgba(255, 255, 255, 0.6)', // ← 主背景透明度
          backdropFilter: 'blur(8px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div 
          className="profile-header"
          style={{
            textAlign: 'center',
            marginBottom: '40px',
            padding: '60px 40px 40px',
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            backdropFilter: 'blur(5px)',
            borderRadius: '0 0 20px 20px',
            borderBottom: '1px solid rgba(24, 144, 255, 0.2)',
          }}
        >
          <div className="header-content">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                {/* 头像部分 */}
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar 
                    size={100} 
                    src={avatar}
                    icon={<UserOutlined />}
                    style={{ 
                      border: '4px solid rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                      marginBottom: '20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    }}
                  />
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    onChange={handleAvatarUpload}
                    customRequest={({ file, onSuccess }) => {
                      setTimeout(() => {
                        onSuccess("ok");
                      }, 0);
                    }}
                  >
                    <Button 
                      type="primary" 
                      shape="circle" 
                      icon={<CameraOutlined />}
                      size="small"
                      style={{
                        position: 'absolute',
                        bottom: '20px',
                        right: '0',
                        width: '32px',
                        height: '32px',
                        backgroundColor: 'rgba(24, 144, 255, 0.9)',
                        borderColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                      }}
                    />
                  </Upload>
                </div>
                
                <Title 
                  level={1} 
                  className="main-title"
                  style={{
                    color: '#1f1f1f',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {username}
                </Title>
              </div>
            </Space>
          </div>
        </div>

        <div className="profile-content">
          <Row gutter={[32, 32]} justify="center">
            <Col xs={24} md={16} lg={12}>
              <Card 
                className="profile-card"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.6)', // ← 卡片透明度
                  backdropFilter: 'blur(5px)',
                  border: '1px solid rgba(232, 232, 232, 0.7)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Tabs defaultActiveKey="profile">
                  <TabPane 
                    tab={
                      <span>
                        <UserOutlined /> 个人信息
                      </span>
                    } 
                    key="profile"
                  >
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleSaveProfile}
                    >
                      <Form.Item
                        label="用户名"
                        name="username"
                        rules={[{ required: true, message: '请输入用户名' }]}
                      >
                        <Input 
                          prefix={<UserOutlined />}
                          placeholder="请输入您的用户名"
                          size="large"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '2px solid rgba(232, 232, 232, 0.7)',
                            borderRadius: '8px',
                          }}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button 
                          type="primary" 
                          htmlType="submit"
                          icon={<SaveOutlined />}
                          loading={loading}
                          size="large"
                          block
                          style={{
                            backgroundColor: 'rgba(24, 144, 255, 0.9)',
                            borderColor: 'rgba(24, 144, 255, 0.9)',
                            backdropFilter: 'blur(3px)',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                          }}
                        >
                          保存个人信息
                        </Button>
                      </Form.Item>
                    </Form>
                  </TabPane>

                  <TabPane 
                    tab={
                      <span>
                        <SafetyOutlined /> 密码安全
                      </span>
                    } 
                    key="password"
                  >
                    <Form
                      layout="vertical"
                      onFinish={handleChangePassword}
                    >
                      <Form.Item
                        label="当前密码"
                        name="oldPassword"
                        rules={[{ required: true, message: '请输入当前密码' }]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder="请输入当前密码"
                          size="large"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.6)',
                            border: '2px solid rgba(232, 232, 232, 0.7)',
                            borderRadius: '8px',
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        label="新密码"
                        name="newPassword"
                        rules={[
                          { required: true, message: '请输入新密码' },
                          { min: 6, message: '密码长度至少6位' }
                        ]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder="请输入新密码（至少6位）"
                          size="large"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '2px solid rgba(232, 232, 232, 0.7)',
                            borderRadius: '8px',
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        label="确认密码"
                        name="confirmPassword"
                        rules={[{ required: true, message: '请确认新密码' }]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder="请再次输入新密码"
                          size="large"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '2px solid rgba(232, 232, 232, 0.7)',
                            borderRadius: '8px',
                          }}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button 
                          type="primary" 
                          htmlType="submit"
                          icon={<SafetyOutlined />}
                          size="large"
                          block
                          style={{
                            backgroundColor: 'rgba(24, 144, 255, 0.9)',
                            borderColor: 'rgba(24, 144, 255, 0.9)',
                            backdropFilter: 'blur(3px)',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                          }}
                        >
                          修改密码
                        </Button>
                      </Form.Item>
                    </Form>
                  </TabPane>
                </Tabs>
                
                <div 
                  style={{ 
                    marginTop: '30px', 
                    paddingTop: '20px', 
                    borderTop: '1px solid rgba(240, 240, 240, 0.7)',
                    backgroundColor: 'rgba(248, 249, 250, 0.9)',
                    backdropFilter: 'blur(3px)',
                    borderRadius: '8px',
                    padding: '20px',
                  }}
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CalendarOutlined style={{ color: 'rgba(24, 144, 255, 0.9)' }} />
                        <Text>注册时间</Text>
                      </div>
                      <Text strong>{registrationDate}</Text>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BookOutlined style={{ color: 'rgba(24, 144, 255, 0.9)' }} />
                        <Text>我的博客</Text>
                      </div>
                      <div>
                        <Text strong style={{ fontSize: '18px', color: 'rgba(24, 144, 255, 0.9)' }}>
                          {postsCount}
                        </Text>
                        <Text style={{ marginLeft: '4px' }}>篇</Text>
                      </div>
                    </div>
                  </Space>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default About;